import { Extension, Mark, mergeAttributes, Node, generateHTML } from "@tiptap/core";
import { DragHandle } from "@tiptap/extension-drag-handle";
import { Image } from "@tiptap/extension-image";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Table, TableKit } from "@tiptap/extension-table";
import StarterKit from "@tiptap/starter-kit";

export function normalizeTextColor(color?: string): string | undefined {
  const rgb = color?.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  const hex = rgb ? "#" + rgb.slice(1).map(value => Number(value).toString(16).padStart(2, "0")).join("") : color;
  return hex && /^#[0-9a-f]{6}$/i.test(hex) ? hex : undefined;
}

const metadataAttribute = (name: string, defaultValue: unknown = null) => ({
  default: defaultValue,
  parseHTML: (element: HTMLElement) => element.getAttribute(`data-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`),
  renderHTML: (attributes: Record<string, unknown>) => attributes[name] == null ? {} : { [`data-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`]: attributes[name] },
});

const BlockMetadata = Extension.create({
  name: "blockMetadata",
  addGlobalAttributes() {
    return [{
      types: ["paragraph", "heading", "table"],
      attributes: {
        blockId: metadataAttribute("blockId"),
        origin: metadataAttribute("origin", "USER"),
        caption: metadataAttribute("caption", ""),
      },
    }];
  },
});

export const ResearchImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      blockId: metadataAttribute("blockId"),
      origin: metadataAttribute("origin", "USER"),
      fileId: metadataAttribute("fileId"),
      originalName: metadataAttribute("originalName"),
      contentType: metadataAttribute("contentType"),
      sizeBytes: metadataAttribute("sizeBytes", 0),
      sha256: metadataAttribute("sha256"),
    };
  },
}).configure({
  resize: { enabled: true, directions: ["bottom-right"], minWidth: 120, minHeight: 80, alwaysPreserveAspectRatio: true },
  HTMLAttributes: { class: "notion-image" },
});

export const Attachment = Node.create({
  name: "attachment",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      blockId: metadataAttribute("blockId"), origin: metadataAttribute("origin", "USER"),
      fileId: metadataAttribute("fileId"), originalName: metadataAttribute("originalName"),
      contentType: metadataAttribute("contentType"), sizeBytes: metadataAttribute("sizeBytes", 0),
      sha256: metadataAttribute("sha256"), caption: metadataAttribute("caption", ""),
    };
  },
  parseHTML() { return [{ tag: "div[data-attachment]" }]; },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-attachment": "", class: "notion-attachment", contenteditable: "false" }),
      ["span", { class: "notion-attachment-icon" }, "↳"],
      ["span", {}, HTMLAttributes.originalName || "첨부 파일"]];
  },
});

const TextColor = Mark.create({
  name: "textColor",
  addAttributes() {
    return { color: { default: null, parseHTML: element => normalizeTextColor(element.style.color) } };
  },
  parseHTML() { return [{ tag: "span[style]", getAttrs: element => element.style.color ? {} : false }]; },
  renderHTML({ HTMLAttributes }) {
    return ["span", /^#[0-9a-f]{6}$/i.test(HTMLAttributes.color || "") ? { style: `color: ${HTMLAttributes.color}` } : {}, 0];
  },
  parseMarkdown: (token, helpers) => helpers.applyMark("textColor", helpers.parseInline(token.tokens || []), { color: token.color }),
  renderMarkdown: (node, helpers) => /^#[0-9a-f]{6}$/i.test(node.attrs?.color || "")
    ? `<span style="color:${node.attrs?.color}">${helpers.renderChildren(node)}</span>` : helpers.renderChildren(node),
  markdownTokenizer: {
    name: "textColor", level: "inline", start: src => src.indexOf('<span style="color:'),
    tokenize(src, _tokens, lexer) {
      const match = /^<span style="color:(#[0-9a-f]{6})">([\s\S]*?)<\/span>/i.exec(src);
      return match ? { type: "textColor", raw: match[0], color: match[1], tokens: lexer.inlineTokens(match[2]) } : undefined;
    },
  },
});

// The previous editor stored underline as __text__, not standard Markdown bold.
const LegacyUnderline = Extension.create({
  name: "legacyUnderline",
  parseMarkdown: (token, helpers) => helpers.applyMark("underline", helpers.parseInline(token.tokens || [])),
  markdownTokenizer: {
    name: "legacyUnderline", level: "inline", start: src => src.indexOf("__"),
    tokenize(src, _tokens, lexer) {
      const match = /^__([^_\n]+)__/.exec(src);
      return match ? { type: "legacyUnderline", raw: match[0], tokens: lexer.inlineTokens(match[1]) } : undefined;
    },
  },
});

const DocumentTable = Table.extend({
  renderMarkdown(node, helpers, context): string {
    // Markdown cannot represent a caption, merged cells, or user-resized columns.
    const hasLayout = node.attrs?.caption || node.content?.some(row => row.content?.some(cell =>
      cell.attrs?.colwidth || cell.attrs?.colspan > 1 || cell.attrs?.rowspan > 1));
    return hasLayout ? generateHTML({ type: "doc", content: [node] }, editorExtensions)
      : Table.config.renderMarkdown!(node, helpers, context);
  },
});

export const editorExtensions = [
  StarterKit.configure({
    codeBlock: false,
    horizontalRule: false,
    link: { openOnClick: false },
  }),
  LegacyUnderline,
  BlockMetadata,
  TextColor,
  TableKit.configure({ table: false }),
  DocumentTable.configure({ resizable: true, HTMLAttributes: { class: "notion-table" } }),
  ResearchImage,
  Attachment,
  Placeholder.configure({ placeholder: "내용을 입력하거나 / 를 눌러 블록을 추가하세요." }),
  DragHandle.configure({
    render: () => {
      const handle = document.createElement("button");
      handle.type = "button";
      handle.className = "notion-drag-handle";
      handle.setAttribute("aria-label", "블록 이동");
      handle.textContent = "⋮⋮";
      return handle;
    },
  }),
];
